import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { getUserPermissions, getUserRole } from '../utils/userPermissions'
import toast from 'react-hot-toast'
import { 
  ArrowLeft,
  DollarSign,
  Filter,
  Search,
  Plus,
  Eye,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
  CreditCard,
  Smartphone,
  FileText,
  Building2,
  TrendingUp,
  RefreshCw,
  XCircle
} from 'lucide-react'
import { Invoice, InvoiceFilters, InvoiceStats } from '../types/financial.types'
import { invoiceService } from '../services/invoiceService'
import { userInvoiceService } from '../services/userInvoiceService'
import { Loading } from '../components/common/Loading'
import { logger } from '../utils/logger'

const Financial: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  
  const permissions = getUserPermissions(user)
  const userRole = getUserRole(user)

  // Estados principais
  const [isLoading, setIsLoading] = useState(false)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [stats, setStats] = useState<InvoiceStats | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
  })

  // Estados de filtros
  const [filters, setFilters] = useState<InvoiceFilters>({
    status: 'all',
    payment_method: 'all',
    type: 'all',
    search: '',
    page: 1,
    limit: 20
  })

  // Estados de UI
  const [showFilters, setShowFilters] = useState(false)
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([])

  // Carregar dados
  useEffect(() => {
    loadInvoices()
    loadStats()
  }, [filters])

  // Debug: verificar stats
  useEffect(() => {
    logger.debug('🔍 Stats state changed', { hasStats: Boolean(stats) })
  }, [stats])

  const loadInvoices = async (showToast = false) => {
    try {
      setIsLoading(true)
      // Usar serviço baseado no tipo de usuário
      const service = userRole === 'attendant' ? userInvoiceService : invoiceService
      const response = await service.getInvoices(filters)
      setInvoices(response.invoices)
      setPagination(response.pagination)
      if (showToast) {
        toast.success(`${response.invoices.length} faturas carregadas com sucesso.`)
      }
    } catch (error) {
      logger.error('Erro ao carregar faturas:', error)
      toast.error('Erro ao carregar faturas. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      logger.debug('🔍 Carregando estatísticas para userRole:', userRole)
      // Usar serviço baseado no tipo de usuário
      if (userRole === 'attendant') {
        const response = await userInvoiceService.getStats()
        logger.debug('📊 Estatísticas do atendente carregadas')
        setStats(response)
      } else {
        // Para admins, usar o serviço de invoice normal
        const response = await invoiceService.getStats()
        logger.debug('📊 Estatísticas do admin carregadas')
        setStats(response)
      }
    } catch (error) {
      logger.error('Erro ao carregar estatísticas:', error)
      toast.error('Erro ao carregar estatísticas. Tente novamente.')
    }
  }

  const handleCreateInvoice = () => {
    if (userRole === 'super_admin') {
      // Super admin pode criar faturas para admins ou clientes
      navigate('/invoices/create')
    } else if (userRole === 'admin_with_subscription') {
      // Admin com assinatura pode criar faturas apenas para clientes (cobranças de serviços)
      navigate('/invoices/create?target=client')
    } else if (userRole === 'attendant') {
      // Atendentes podem criar faturas apenas para clientes
      navigate('/invoices/create?target=client')
    } else {
      // Outros usuários não podem criar faturas
      toast.error('Você não tem permissão para criar faturas.')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-100'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      case 'expired':
        return 'text-gray-600 bg-gray-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return CheckCircle
      case 'pending':
        return Clock
      case 'cancelled':
        return X
      case 'expired':
        return AlertCircle
      default:
        return Clock
    }
  }

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
        return CreditCard
      case 'pix':
        return Smartphone
      case 'boleto':
        return FileText
      default:
        return DollarSign
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }


  // Função comentada - não está sendo usada no momento
  // const handleBulkAction = async (action: string) => {
  //   if (selectedInvoices.length === 0) return

  //   try {
  //     setIsLoading(true)
  //     // Implementar ações em lote
  //     setSelectedInvoices([])
  //     await loadInvoices()
  //   } catch (error) {
  //     logger.error('Erro ao executar ação em lote:', error)
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  const handleSelectInvoice = (invoiceId: number) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    )
  }

  const handleSelectAll = () => {
    if (selectedInvoices.length === (invoices?.length || 0)) {
      setSelectedInvoices([])
    } else {
      setSelectedInvoices((invoices || []).map(invoice => Number(invoice.id)))
    }
  }

  const handleSearch = (searchTerm: string) => {
    setFilters({...filters, search: searchTerm, page: 1})
  }

  if (isLoading && (invoices?.length || 0) === 0) {
    return <Loading fullScreen />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              aria-label="Voltar para o dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Financeiro
              </h1>
              <p className="text-gray-600">Gerencie suas faturas e pagamentos</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {(permissions.canCreateInvoicesForAdmins || permissions.canCreateInvoicesForClients) && (
              <button
                onClick={handleCreateInvoice}
                className="btn btn-primary btn-md"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Fatura
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Faturas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.total}
                </p>
              </div>
              <div className="bg-blue-100 rounded-lg p-3">
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pagas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.paid}
                </p>
              </div>
              <div className="bg-green-100 rounded-lg p-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.pending}
                </p>
              </div>
              <div className="bg-yellow-100 rounded-lg p-3">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Canceladas</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.cancelled || 0}
                </p>
              </div>
              <div className="bg-red-100 rounded-lg p-3">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Receita Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency((stats as any)?.total_revenue || 0)}
                </p>
              </div>
              <div className="bg-green-100 rounded-lg p-3">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </div>
        </div>
      )}
    

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>{showFilters ? 'Ocultar' : 'Mostrar'} Filtros</span>
          </button>
        </div>

        {/* Busca */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por descrição, cliente ou ID..."
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendente</option>
                <option value="paid">Pago</option>
                <option value="cancelled">Cancelado</option>
                <option value="expired">Expirado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Método de Pagamento
              </label>
              <select
                value={filters.payment_method}
                onChange={(e) => setFilters({...filters, payment_method: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                <option value="credit_card">Cartão de Crédito</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                {userRole === 'super_admin' && (
                  <>
                    <option value="admin">Admin</option>
                    <option value="client">Cliente</option>
                  </>
                )}
                {userRole === 'admin_with_subscription' && (
                  <option value="client">Cliente</option>
                )}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Lista de Faturas */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Faturas ({pagination?.total || 0})
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => loadInvoices(true)}
                className="btn btn-outline btn-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6">
            <Loading />
          </div>
        ) : invoices && invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length === (invoices?.length || 0)}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vencimento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(invoices || []).map((invoice) => {
                  const StatusIcon = getStatusIcon(invoice.status)
                  const PaymentIcon = getPaymentMethodIcon(invoice.payment_method)
                  
                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(Number(invoice.id))}
                          onChange={() => handleSelectInvoice(Number(invoice.id))}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{invoice.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.customer_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {invoice.customer_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {invoice.status === 'paid' ? 'Pago' :
                           invoice.status === 'pending' ? 'Pendente' :
                           invoice.status === 'cancelled' ? 'Cancelado' :
                           invoice.status === 'expired' ? 'Expirado' : invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <PaymentIcon className="h-4 w-4 mr-2" />
                          {invoice.payment_method === 'credit_card' ? 'Cartão Credito' :
                          invoice.payment_method === 'debit_card' ? 'Cartão Debito' :
                           invoice.payment_method === 'pix' ? 'PIX' :
                           invoice.payment_method === 'boleto' ? 'Boleto' : invoice.payment_method}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(invoice.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                              onClick={() => navigate(`/invoices/${invoice.id}`)}
                              className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {invoice.status === 'pending' && (
                              <button
                                onClick={() => navigate(`/invoices/${invoice.id}/payment`)}
                                className="text-green-600 hover:text-green-800 transition-colors duration-200"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma fatura encontrada</h3>
            <p className="text-gray-500 mb-4">
              {userRole === 'super_admin' && 'Crie faturas para administradores ou clientes'}
              {userRole === 'admin_with_subscription' && 'Crie faturas para clientes (cobranças de serviços)'}
              {userRole === 'attendant' && 'Não há faturas disponíveis para visualização'}
            </p>
            {(permissions.canCreateInvoicesForAdmins || permissions.canCreateInvoicesForClients) && (
              <button
                onClick={handleCreateInvoice}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeira Fatura
              </button>
            )}
          </div>
        )}

        {/* Paginação */}
        {pagination?.total_pages && pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((pagination?.page || 1) - 1) * (pagination?.limit || 20) + 1} a {Math.min((pagination?.page || 1) * (pagination?.limit || 20), pagination?.total || 0)} de {pagination?.total || 0} resultados
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setFilters({...filters, page: (pagination?.page || 1) - 1})}
                  disabled={(pagination?.page || 1) === 1}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-700">
                  Página {pagination?.page || 1} de {pagination?.total_pages || 1}
                </span>
                <button
                  onClick={() => setFilters({...filters, page: (pagination?.page || 1) + 1})}
                  disabled={(pagination?.page || 1) === (pagination?.total_pages || 1)}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

export default Financial
